







































































































































































































































































































































































































































import { Component, Prop, PropSync, Watch, Vue } from "vue-property-decorator";
import store from "@/store";
//  @utilities
import moment from "moment";
import numberHelper from "Utilities/number-helper";
import htmlEncoder from "Utilities/htmlencode-helper";
// @types
import { VesselAccessGroup } from "@/types/vesselAccessGroup";
import { Vessel } from "@/types/vessel";
import { UserProfile } from "@/types/userProfile";
//  @clients
import VesselAccessGroupsClient from "Clients/vessel-categories-client";
//  @modules
import { getModule } from "vuex-module-decorators";
import SnackbarModule from "@/store/clients/Snackbar.module";

const Snackbar = getModule(SnackbarModule, store);

@Component({
  components: {},
})
export default class VesselAccessGroupDataModal extends Vue {
  @PropSync("value", { required: true, type: Boolean, default: false }) isModalActive!: boolean;
  @Prop({ type: Array, default: () => [], required: true }) vesselAccessGroups!: any[];
  @PropSync("vesselAccessGroupToEditId", { type: Number, default: null }) syncVesselAccessGroupToEditId!: number | null;
  @Prop({ type: String, default: "463px" }) width!: string;

  // @Data
  formData: Partial<VesselAccessGroup> = {};
  initialVesselList: Vessel[] = [];
  initialUserList: UserProfile[] = [];
  isVesselsSelectMenuOpen = false;
  isUsersSelectMenuOpen = false;
  searchVesselQuery: string = "";
  searchUserQuery: string = "";

  // @Watchers
  @Watch("isModalActive")
  onModalActiveChanged(isOpen: boolean): void {
    if (isOpen) this.initializeForm();
  }

  //  @Validations
  fieldRequired = {
    required: (value: any) => {
      // Allow 0 as valid value for numeric fields
      if (value === 0) return true;
      // Check if value exists (not null, undefined, or empty string)
      return !!value || "Field is required";
    },
    nameMaxLength: (value: any) => {
      // allow required() to handle empty - here validate length when present
      if (value === null || value === undefined || String(value).trim() === "") return true;
      return String(value).length <= 30 || "May not be longer than 30 characters";
    },
  };

  // @Getters
  get vesselAccessGroupVesselList(): Vessel[] {
    const vesselAccessGroup: VesselAccessGroup = this.vesselAccessGroups.find(g => g.id === this.syncVesselAccessGroupToEditId);
    if (!vesselAccessGroup) return [] as Vessel[];
    return vesselAccessGroup.vessels ?? ([] as Vessel[]);
  }

  get vessels(): Vessel[] {
    return store.getters["Vessels/vessels"] || [];
  }

  get filteredVessels(): Vessel[] {
    const query = this.searchVesselQuery.trim().toLowerCase();
    if (!query) return this.vessels;
    return this.vessels.filter(vessel => vessel.name.toLowerCase().includes(query));
  }

  get selectedVesselsList(): Vessel[] {
    // Filter out any undefined or null items from formData.vessels
    if (!this.formData.vessels) return [];
    return this.formData.vessels.filter((v): v is Vessel => v != null && v.id != null);
  }

  get isAllVesselsSelected(): boolean {
    if (!this.formData.vessels || this.vessels.length === 0) return false;
    return this.formData.vessels.length === this.vessels.length;
  }

  get isSomeVesselsSelected(): boolean {
    if (!this.formData.vessels) return false;
    return this.formData.vessels.length > 0 && this.formData.vessels.length < this.vessels.length;
  }

  get selectAllCheckboxState(): boolean | null {
    if (this.isAllVesselsSelected) return true;
    if (this.isSomeVesselsSelected) return null; // indeterminate state
    return false;
  }

  get vesselsSelectedText(): string {
    const count = this.formData.vessels?.length ?? 0;
    return count === 1 ? "1 vessel selected" : `${count} vessels selected`;
  }

  get users(): UserProfile[] {
    return store.getters["Users/users"] || [];
  }

  get filteredUsers(): UserProfile[] {
    const query = this.searchUserQuery.trim().toLowerCase();
    if (!query) return this.users;
    return this.users.filter(user => (user.name && user.name.toLowerCase().includes(query)) || (user.email && user.email.toLowerCase().includes(query)));
  }

  get selectedUsersList(): UserProfile[] {
    // Filter out any undefined or null items from formData.users
    if (!this.formData.users) return [];
    return this.formData.users.filter((u): u is UserProfile => u != null && u.id != null);
  }

  get isAllUsersSelected(): boolean {
    if (!this.formData.users || this.users.length === 0) return false;
    return this.formData.users.length === this.users.length;
  }

  get isSomeUsersSelected(): boolean {
    if (!this.formData.users) return false;
    return this.formData.users.length > 0 && this.formData.users.length < this.users.length;
  }

  get selectAllUsersCheckboxState(): boolean | null {
    if (this.isAllUsersSelected) return true;
    if (this.isSomeUsersSelected) return null; // indeterminate state
    return false;
  }

  get usersSelectedText(): string {
    const count = this.formData.users?.length ?? 0;
    return count === 1 ? "1 user selected" : `${count} users selected`;
  }

  get isNewVesselAccessGroup(): boolean {
    return this.syncVesselAccessGroupToEditId === null;
  }

  get ModalMode(): any {
    return {
      mode: this.isNewVesselAccessGroup ? "add" : "edit",
      title: this.isNewVesselAccessGroup ? "Add Vessel Access Group" : "Edit Vessel Access Group",
      btnText: this.isNewVesselAccessGroup ? "Add Vessel Access Group" : "Update",
    };
  }

  get formattedVesselsSelectionResult(): string {
    const vessels = this.formData.vessels?.length ?? 0;
    if (vessels === 0) return "";
    return vessels === 1 ? "Vessel (1)" : `Vessels (${vessels})`;
  }

  get formattedUsersSelectionResult(): string {
    const users = this.formData.users?.length ?? 0;
    if (users === 0) return "";
    return users === 1 ? "User (1)" : `Users (${users})`;
  }

  get isSubmitDisabled(): boolean {
    // Use bound formData properties (template uses formData.*)
    const name = this.formData.name ?? "";

    const v = this.fieldRequired;

    // validator returns true (valid) or an error string -> treat only true as valid
    const ok = (validator: Function, value: any) => {
      try {
        return validator(value) === true;
      } catch {
        return false;
      }
    };

    const nameOk = ok(v.required, name) && ok(v.nameMaxLength, name);

    return !nameOk;
  }

  // @Methods
  // Initialize form data based on whether we're creating or editing
  initializeForm(): void {
    if (this.isNewVesselAccessGroup) {
      // New: reset form with all fields initialized
      this.formData = {
        name: "",
        vessels: [] as Vessel[],
        users: [],
      };

      // Reset all field validations for add mode
      this.$nextTick(() => {
        this.$nextTick(() => {
          const form = this.$el.querySelector(".dfi__form");
          if (form) {
            const textFields = form.querySelectorAll(".v-text-field");
            textFields.forEach((field: any) => {
              if (field.__vue__ && field.__vue__.resetValidation) {
                field.__vue__.resetValidation();
              }
            });
          }
        });
      });
      this.initialVesselList = [];
      this.initialUserList = [];
    } else {
      // Edit mode: populate form with user data
      const vesselAccessGroup = this.vesselAccessGroups.find(g => g.id === this.syncVesselAccessGroupToEditId);
      if (vesselAccessGroup) {
        // Clone company data and
        this.formData = {
          id: vesselAccessGroup.id || null,
          name: vesselAccessGroup.name || "",
          users: vesselAccessGroup.users || [],
          vessels: (vesselAccessGroup.vessels || []).filter((v: any): v is Vessel => v != null && v.id != null),
          isDefault: vesselAccessGroup.isDefault || false,
        };
      }

      this.initialVesselList = this.formData.vessels ? [...this.formData.vessels] : [];
      this.initialUserList = this.formData.users ? [...this.formData.users] : [];
    }
  }

  toggleSelectAll(): void {
    if (this.isAllVesselsSelected) {
      // Deselect all
      this.formData.vessels = [];
    } else {
      // Select all vessels
      this.formData.vessels = [...this.vessels];
    }
  }

  toggleSelectAllUsers(): void {
    if (this.isAllUsersSelected) {
      // Deselect all
      this.formData.users = [];
    } else {
      // Select all users
      this.formData.users = [...this.users];
    }
  }

  removeVessel(vesselId: number): void {
    if (!this.formData.vessels) return;
    this.formData.vessels = this.formData.vessels.filter(v => v.id !== vesselId);
  }

  removeUser(userId: number | string): void {
    if (!this.formData.users) return;
    this.formData.users = this.formData.users.filter(u => u.id !== userId);
  }

  clearVesselSelection(): void {
    this.formData.vessels = [];
  }

  clearUserSelection(): void {
    this.formData.users = [];
  }

  close(): void {
    this.isModalActive = false;
    this.$emit("update:vesselAccessGroupToEditId", null);
    // Also reset form when canceling
    setTimeout(() => {
      this.resetForm();
    }, 300);
  }

  cancel(): void {
    this.isModalActive = false;
    // Reset vesselAccessGroupToEditId in parent when canceling
    this.$emit("update:vesselAccessGroupToEditId", null);
    // Also reset form when canceling
    setTimeout(() => {
      this.resetForm();
    }, 300);
  }

  async submit(): Promise<void> {
    try {
      if (this.isNewVesselAccessGroup) {
        const createdVesselAccessGroup = await VesselAccessGroupsClient.create(this.formData);
        for (const vessel of this.formData.vessels || []) {
          await VesselAccessGroupsClient.addVesselToCategory(createdVesselAccessGroup, vessel);
        }
        for (const user of this.formData.users || []) {
          await VesselAccessGroupsClient.addUserToCategory(createdVesselAccessGroup, user);
        }
      } else {
        // Update mode: handle vessel and user changes
        this.formData = {
          ...this.formData,
          id: this.syncVesselAccessGroupToEditId!,
        };

        // Update the vessel access group name/details
        await VesselAccessGroupsClient.update(this.formData);

        // Get the full vesselAccessGroup object for API calls
        const vesselAccessGroup = this.vesselAccessGroups.find(g => g.id === this.syncVesselAccessGroupToEditId);
        if (!vesselAccessGroup) {
          throw new Error("Vessel Access Group not found");
        }

        const currentVessels = this.formData.vessels || [];
        const currentUsers = this.formData.users || [];

        // Find vessels to add (in current but not in initial)
        const vesselsToAdd = currentVessels.filter(vessel => !this.initialVesselList.some(initial => initial.id === vessel.id));

        // Find vessels to remove (in initial but not in current)
        const vesselsToRemove = this.initialVesselList.filter(initial => !currentVessels.some(vessel => vessel.id === initial.id));

        // Find users to add (in current but not in initial)
        const usersToAdd = currentUsers.filter(user => !this.initialUserList.some(initial => initial.id === user.id));

        // Find users to remove (in initial but not in current)
        const usersToRemove = this.initialUserList.filter(initial => !currentUsers.some(user => user.id === initial.id));

        // Add new vessels
        for (const vessel of vesselsToAdd) {
          await VesselAccessGroupsClient.addVesselToCategory(vesselAccessGroup, vessel);
        }

        // Remove vessels
        for (const vessel of vesselsToRemove) {
          await VesselAccessGroupsClient.removeVesselFromCategory(vesselAccessGroup, vessel);
        }

        // Add new users
        for (const user of usersToAdd) {
          await VesselAccessGroupsClient.addUserToCategory(vesselAccessGroup, user);
        }

        // Remove users
        for (const user of usersToRemove) {
          await VesselAccessGroupsClient.removeUserFromCategory(vesselAccessGroup, user);
        }
      }

      this.$emit("onSubmit", { vesselAccessGroupData: this.formData, isNewVesselAccessGroup: this.isNewVesselAccessGroup });
      Snackbar.showSnackbar({
        text: `Vessel Access Group <b>${htmlEncoder.htmlEncode(this.formData.name)}</b> successfully ${this.isNewVesselAccessGroup ? "created" : "updated"}`,
        color: "success",
      });
    } catch (error) {
      Snackbar.showSnackbar({ text: "Failed to submit vessel access group data", color: "error" });
    } finally {
      this.isModalActive = false;
      // Reset vesselAccessGroupToEditId in parent after submission
      this.$emit("update:vesselAccessGroupToEditId", null);
      // Reset form and validations
      this.$nextTick(() => {
        this.resetForm();
      });
    }
  }

  // Reset form data and validations
  resetForm(): void {
    // Reset form data to initial state
    this.formData = {
      name: "",
      vessels: [] as Vessel[],
      users: [],
    };

    // Reset all field validations
    this.$nextTick(() => {
      const fields = ["name", "vessels", "users"];
      fields.forEach(fieldRef => {
        const field = (this.$refs as any)[fieldRef];
        if (field && field.resetValidation) {
          field.resetValidation();
        }
      });

      // Also reset v-text-fields in the form
      const form = this.$el?.querySelector?.(".dfi__form");
      if (form) {
        const textFields = form.querySelectorAll(".v-text-field");
        textFields.forEach((field: any) => {
          if (field.__vue__ && field.__vue__.resetValidation) {
            field.__vue__.resetValidation();
          }
        });
      }
    });
  }

  numericFilter(event: any): any {
    return numberHelper.numericRoundFilter(event);
  }

  // @Hooks
  async mounted(): Promise<void> {
    store.dispatch("Vessels/refreshVessels");
    store.dispatch("Users/refreshUsers");
    this.initializeForm();
  }
}
